import { MusicClock } from './MusicClock.js';

/**
 * Owns the audio graph and the clock derived from it.
 *
 * The graph is deliberately tiny for this phase:
 *
 *   music -> musicGain -> masterGain -> destination
 *
 * with a separate `sfxGain` already branched off the master so effects can be
 * mixed later without restructuring anything.
 *
 * Browsers refuse to start an `AudioContext` outside a user gesture, so this is
 * built suspended and `resume()` must be called from a real click. That is not a
 * detail to paper over: the whole point of Phase 1 is that audio and video start
 * together, and the only moment both are permitted to start is inside a gesture
 * handler.
 */

/**
 * Level for the effects bus, relative to the music bus at 0.85.
 *
 * Lowered from 0.5 after playing it: the level chosen from the numbers alone
 * was correct on paper and too assertive in practice. Against the reference
 * pack the song peaks at 0.574 and averages 0.123 RMS, and note-hit.wav peaks
 * at 0.867; at this gain the effect lands at 0.217, still roughly twice the
 * song's average level so it reads clearly, but well under its peaks instead of
 * beside them.
 *
 * A starting point, not a verdict — the player owns this now, from Settings.
 */
export const DEFAULT_SFX_VOLUME = 0.25;

export interface AudioEngineOptions {
  /** An existing context to adopt. The host owns it if supplied. */
  readonly context?: AudioContext;
  readonly masterVolume?: number;
  readonly musicVolume?: number;
  readonly sfxVolume?: number;
}

export class AudioEngine {
  readonly context: AudioContext;
  readonly master: GainNode;
  readonly music: GainNode;
  readonly sfx: GainNode;
  readonly clock: MusicClock;

  private readonly ownsContext: boolean;
  private disposed = false;
  audioError: string | null = null;
  audioSessionType = 'unsupported';
  private outputProbe: AnalyserNode | null = null;
  private readonly outputSamples = new Float32Array(256);
  private outputPeak = 0;

  /** Post-master signal evidence, NOT proof of sound at the physical speaker. */
  outputSignalPeak(): number {
    if (this.outputProbe !== null) {
      this.outputProbe.getFloatTimeDomainData(this.outputSamples);
      for (const value of this.outputSamples) this.outputPeak = Math.max(this.outputPeak, Math.abs(value));
    }
    return this.outputPeak;
  }

  get platform(): string {
    return typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent;
  }

  /** iOS Web Audio otherwise obeys the ringer switch even while running. */
  private configurePlaybackSession(): void {
    if (typeof navigator === 'undefined') return;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (!ios || session === undefined) return;
    try { session.type = 'playback'; this.audioSessionType = session.type; }
    catch (cause) { this.audioError = `Audio Session: ${String(cause)}`; }
  }

  constructor(options: AudioEngineOptions = {}) {
    if (options.context !== undefined) {
      this.context = options.context;
      this.ownsContext = false;
    } else {
      this.context = new AudioContext({ latencyHint: 'interactive' });
      this.ownsContext = true;
    }

    this.master = this.context.createGain();
    this.master.gain.value = options.masterVolume ?? 1;
    this.master.connect(this.context.destination);

    this.music = this.context.createGain();
    this.music.gain.value = options.musicVolume ?? 0.85;
    this.music.connect(this.master);

    this.sfx = this.context.createGain();
    this.sfx.gain.value = options.sfxVolume ?? DEFAULT_SFX_VOLUME;
    this.sfx.connect(this.master);

    this.clock = new MusicClock(this.context);
  }

  /** Music level, 0..1. Independent of the effects bus. */
  get musicVolume(): number {
    return this.music.gain.value;
  }
  set musicVolume(value: number) {
    this.music.gain.value = Math.max(0, value);
  }

  /** Effects level, 0..1. Independent of the music bus. */
  get sfxVolume(): number {
    return this.sfx.gain.value;
  }
  set sfxVolume(value: number) {
    this.sfx.gain.value = Math.max(0, value);
  }

  get state(): AudioContextState {
    return this.context.state;
  }

  get running(): boolean {
    return this.context.state === 'running';
  }

  /**
   * Unlock both the context and its output in the original mouse/touch/pen
   * gesture, before downloading or decoding a song. Some WebKit hosts keep
   * their output restricted until a source has actually been started.
   */
  resumeFromGesture(): Promise<boolean> {
    if (this.disposed || this.state === 'closed') return Promise.resolve(false);
    this.audioError = null;
    this.configurePlaybackSession();
    this.outputPeak = 0;
    if (this.outputProbe === null && typeof this.context.createAnalyser === 'function') {
      this.outputProbe = this.context.createAnalyser();
      this.outputProbe.fftSize = 256;
      this.master.connect(this.outputProbe);
    }
    try {
      const source = this.context.createBufferSource();
      source.buffer = this.context.createBuffer(1, 1, this.context.sampleRate);
      source.connect(this.master);
      source.onended = () => source.disconnect();
      source.start();
    } catch {
      // Context resume can still succeed on hosts without this output unlock.
    }
    return this.resume();
  }

  /** A denied/interrupted resume must never leave PLAY DEMO busy forever. */
  async resume(timeoutMs = 4000): Promise<boolean> {
    // Read through the getter both times: a direct `state` comparison narrows the
    // type at the first check and the compiler then treats the second as dead,
    // unaware that `resume()` is what changes it.
    if (this.disposed || this.state === 'closed') return false;
    if (this.running) return true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const ready = await Promise.race([
        this.context.resume().then(() => true),
        new Promise<boolean>((resolve) => { timeout = setTimeout(() => resolve(false), timeoutMs); }),
      ]);
      if (!ready || !this.running) this.audioError = `AudioContext resume failed/timed out (${this.state})`;
      return ready && this.running;
    } catch (cause) {
      this.audioError = `AudioContext resume: ${String(cause)}`;
      return false;
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }
  }

  /** Freeze audio and the audio-derived chart clock together while backgrounded. */
  async suspend(): Promise<void> {
    if (this.disposed || !this.running) return;
    try { await this.context.suspend(); } catch { /* A host may own suspension. */ }
  }

  /**
   * Decodes compressed or PCM audio.
   *
   * `decodeAudioData` **detaches** the ArrayBuffer it is given, which is why the
   * songpack layer hands out a fresh copy on every call rather than its own
   * storage: decoding once would otherwise destroy the loaded pack.
   */
  async decode(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.context.decodeAudioData(data);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.music.disconnect();
    this.sfx.disconnect();
    this.master.disconnect();
    this.outputProbe?.disconnect();
    // Only close a context this engine created; a host-supplied one belongs to
    // the host and may outlive the session.
    if (this.ownsContext) void this.context.close();
  }
}
